import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  BookmarkCreate,
  ButtonGroupNav,
  ClearButton,
  InfoIcon,
  InfoIconUrl,
  PermissionCode,
  useHasPermission,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { useSectionContext } from '../../common/SectionContext';
import translations from './InputDataHeader.translations.json';
import { InputDataUpload, InputDataUploadProps } from './InputDataUpload';
import { Helmet } from 'react-helmet-async';
import { getPeriodToDays } from './utils';
import React, { useMemo, useState } from 'react';

const { addTranslations, useTranslation } = i18nNsFactory('input-data-header');
addTranslations(translations);

export interface InputDataHeaderProps {
  valueDate?: [string, string];
  filtersCount: number;
  onFilterClear: () => void;
  onFilterToggle?: () => void;
  isFilterOpen?: boolean;
  onRefresh?: () => void;
  upload?: InputDataUploadProps;
  infoIconUrl?: InfoIconUrl;
}

export function InputDataHeader({
  valueDate,
  filtersCount,
  onFilterClear,
  onFilterToggle,
  isFilterOpen,
  onRefresh,
  upload,
  infoIconUrl,
}: InputDataHeaderProps) {
  const { t } = useTranslation();
  const { basePath } = useSectionContext();

  const hasPermissionToManageInputData = useHasPermission(
    PermissionCode.INPUT_DATA_MANAGE,
  );

  const valueDays = useMemo(() => getPeriodToDays(valueDate), [valueDate]);
  const [isUploadPending, setIsUploadPending] = useState(false);

  return (
    <>
      <Helmet>
        <title>{t('header')}</title>
      </Helmet>
      <Flex justify="space-between">
        <Flex gap="middle">
          <Title>{t('header')}</Title>
          {infoIconUrl && <InfoIcon infoIconUrl={infoIconUrl}></InfoIcon>}
          <BookmarkCreate
            excludeUrlParams={['valueDate']}
            includeUrlParams={
              !valueDays
                ? []
                : [{ key: 'valueDays', value: valueDays.toString() }]
            }
          />
          <ButtonGroupNav
            items={[
              {
                key: 'processingState',
                label: t('view.processingState'),
                to: basePath,
                disabled: isUploadPending,
              },
              {
                key: 'errors',
                label: t('view.errors'),
                to: `${basePath}/errors`,
                disabled: isUploadPending,
              },
            ]}
          />
          {hasPermissionToManageInputData && upload && (
            <InputDataUpload {...upload} onPendingChange={setIsUploadPending} />
          )}
        </Flex>
        <div>
          <Flex gap="small" justify="end">
            {!!onRefresh && (
              <Button
                id="refresh-directory"
                onClick={() => onRefresh()}
                disabled={isUploadPending}
              >
                <Flex gap="small" align="center">
                  {t('button.refresh')}
                </Flex>
              </Button>
            )}
            {filtersCount > 0 && (
              <ClearButton
                id="clear-input-data-dir-filters"
                count={filtersCount}
                onClick={onFilterClear}
              />
            )}
            <Button
              id="toggle-directory-filter"
              type="primary"
              onClick={onFilterToggle}
            >
              <Flex gap="small" align="center">
                <Icon.Filter size={14} />
                {isFilterOpen
                  ? t('button.close-filter')
                  : t('button.open-filter')}
              </Flex>
            </Button>
          </Flex>
        </div>
      </Flex>
    </>
  );
}

const Title = styled.h3`
  align-self: flex-start;
`;
